import { Check, LinkSimple, ShareNetwork } from '@phosphor-icons/react';
import classNames from 'classnames';
import { motion } from 'motion/react';

import { PlatformShareButton } from '../ui/PlatformShareButton';

import { useCopyToClipboard } from '../utils/useCopyToClipboard';
import { useIsClient } from '../utils/useIsClient';

import type { TipJarCopy } from './TipJar.types';
import * as styles from './TipJarShare.css';
import { tokens } from './tipJarTheme.css';

const SHARE_PLATFORMS = ['x', 'threads', 'whatsApp', 'linkedIn', 'meta'];

type TipJarShareProps = {
  url: string;
  text: string;
  copy: Pick<TipJarCopy, 'shareHeading' | 'shareBody' | 'share' | 'copyLink' | 'linkCopied'>;
};

export const TipJarShare = ({ url, text, copy }: TipJarShareProps) => {
  const isClient = useIsClient();
  const { copied, copy: copyToClipboard } = useCopyToClipboard();
  const canShareNatively = isClient && typeof navigator.share === 'function';

  const shareNatively = async () => {
    try {
      await navigator.share({ text, url });
    } catch {
      // The reader closed the share sheet.
    }
  };

  return (
    <motion.section
      className={styles.share}
      animate={{ scale: [1, 1.02, 1] }}
      transition={{ duration: 0.6, delay: 0.8 }}
    >
      <h2 className={styles.heading}>{copy.shareHeading}</h2>
      {copy.shareBody ? <p className={styles.body}>{copy.shareBody}</p> : null}

      <div className={styles.actions}>
        {canShareNatively && (
          <button type="button" className={classNames(styles.action, styles.primaryAction)} onClick={shareNatively}>
            <ShareNetwork size={18} weight="bold" aria-hidden />
            {copy.share}
          </button>
        )}
        <button
          type="button"
          className={classNames(styles.action, canShareNatively ? styles.secondaryAction : styles.primaryAction)}
          onClick={() => copyToClipboard(url)}
        >
          {copied ? <Check size={18} weight="bold" aria-hidden /> : <LinkSimple size={18} weight="bold" aria-hidden />}
          <span aria-live="polite">{copied ? copy.linkCopied : copy.copyLink}</span>
        </button>
      </div>

      <div className={styles.platforms}>
        {SHARE_PLATFORMS.map((platform) => (
          <PlatformShareButton
            key={platform}
            platform={platform}
            title={text}
            url={url}
            className={styles.platform}
            // The share buttons reset their own border inline, so the ring is set the same way.
            borderStyle="solid"
            borderWidth="1px"
            borderColor={tokens.line}
            borderRadius="999px"
            color="currentColor"
          />
        ))}
      </div>
    </motion.section>
  );
};
